// The board: groups down the page, one row per task, subitems nested under the
// task they belong to. Modelled on the monday.com board it replaces, because
// six people already know how to read that and a migration that also retrains
// everybody is two changes, not one.
//
// SEVEN GROUPS, NOT FIVE, SINCE 23 AUGUST
//
// Open used to hold three different situations at once: 29 tasks nobody had
// started, 16 somebody was in the middle of, and 7 stuck behind somebody else.
// Blocked and Ongoing are now their own groups and the group follows the status,
// so Open means Not Started and means it every time. Standing keeps its
// exemption while the work is live, because a rule that pulled a recurring task
// out the moment somebody started it would empty the group in a week. A Standing
// card marked Done or Cancelled does leave, since db/migrations/0117: a finished
// or killed card sitting in the group the team reads every morning is the board
// saying something untrue. See db/migrations/0023 and 0117.
import { useMemo, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react'
import {
  ChevronDown,
  ChevronRight,
  CornerDownRight,
  MessageSquare,
  Paperclip,
  Plus,
  RotateCcw,
  TableProperties,
  TriangleAlert,
  WrapText,
  AtSign,
} from 'lucide-react'
import { cn } from '../../lib/shadcn/utils'
import {
  BOARD_COLUMNS,
  boardWidth,
  clampWidth,
  clearColumnWidths,
  columnTemplate,
  defaultColumnWidths,
  readColumnWidths,
  readWrapNames,
  widthsAreDefault,
  writeColumnWidths,
  writeWrapNames,
  type ColumnKey,
  type ColumnWidths,
} from './boardColumns'
import {
  originOf,
  GROUP_BAR,
  GROUP_LABEL,
  GROUP_NOTE,
  daysDeltaLabel,
  daysDeltaShort,
  groupRows,
  linksOf,
  longDateTime,
  onTimeLabel,
  type BoardGroup,
  type LinkKind,
  type TaskLinkOption,
  type TaskPerson,
  type TaskRow,
} from '../../utils/tasks'
import {
  AuthorityCell,
  Cell,
  DateCell,
  LinkCell,
  PeopleCell,
  PriorityCell,
  StatusCell,
  TitleCell,
  type SavePatch,
  StampCell,
} from './TaskCells'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../lib/shadcn/tooltip'

// One row shape shared by the header and every row, so the columns line up
// without a table element. A table would be simpler until the first nested
// subitem row, which has to indent inside the first column only.
//
// The column template is no longer in this string. It is built from the widths
// the person dragged; see boardColumns.ts for why those are a preference and
// not a database column.
const ROW_CLASS = 'grid items-center gap-1'

/**
 * The header, which is also where the columns are resized.
 *
 * A drag handle sits on the right edge of every column, not only the task name:
 * the name is the one that hurt most, but "Vehicle / owner" truncates two chips
 * down to "+2" at 144px and the driver column loses surnames. Double clicking a
 * handle puts that one column back to its default, which is the cheap way out
 * of a mis-drag without resetting the whole board.
 */
function HeaderRow({
  widths,
  resizing,
  wrapNames,
  onResizeStart,
  onResetColumn,
  onResetAll,
  onToggleWrap,
}: {
  widths: ColumnWidths
  resizing: ColumnKey | null
  wrapNames: boolean
  onResizeStart: (e: ReactPointerEvent, key: ColumnKey) => void
  onResetColumn: (key: ColumnKey) => void
  onResetAll: () => void
  onToggleWrap: () => void
}) {
  return (
    <div
      className={cn(
        ROW_CLASS,
        'sticky top-0 z-20 border-b border-border bg-card px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground',
      )}
      style={{ gridTemplateColumns: columnTemplate(widths) }}
    >
      {BOARD_COLUMNS.map((c) => (
        <div
          key={c.key}
          className={cn(
            'relative flex min-w-0 items-center self-stretch px-2',
            c.align === 'center' && 'justify-center',
            c.key === 'task' && 'pl-6',
          )}
        >
          <span className="truncate">{c.label}</span>

          {/* The two board-wide controls live in the Task header because that
              is the column people come here to widen, and a control nobody can
              find is a control nobody uses. */}
          {c.key === 'task' && (
            <span className="ml-1.5 flex shrink-0 items-center gap-0.5 normal-case">
              <button
                type="button"
                onClick={onToggleWrap}
                aria-pressed={wrapNames}
                title={
                  wrapNames
                    ? 'Task names are wrapping onto up to three lines. Click to keep them on one.'
                    : 'Task names are cut off at one line. Click to let them wrap onto up to three.'
                }
                className={cn(
                  'rounded p-0.5 hover:bg-muted hover:text-foreground',
                  wrapNames && 'bg-muted text-foreground',
                )}
              >
                <WrapText className="h-3.5 w-3.5" />
              </button>
              {!widthsAreDefault(widths) && (
                <button
                  type="button"
                  onClick={onResetAll}
                  title="Put every column back to its default width"
                  className="rounded p-0.5 hover:bg-muted hover:text-foreground"
                >
                  <TableProperties className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          )}

          {/* Pointer events rather than mouse events, so one path covers a
              mouse, a trackpad and a finger. touch-none stops a drag on a phone
              from scrolling the board underneath it at the same time. */}
          <span
            role="separator"
            aria-orientation="vertical"
            aria-label={`Resize the ${c.label} column`}
            title="Drag to resize. Double click to put this column back."
            onPointerDown={(e) => onResizeStart(e, c.key)}
            onDoubleClick={() => onResetColumn(c.key)}
            className={cn(
              'absolute -right-1 top-0 z-10 h-full w-2 cursor-col-resize touch-none select-none',
              'after:absolute after:left-1/2 after:top-1 after:h-[calc(100%-0.5rem)] after:w-px',
              'after:-translate-x-1/2 after:bg-border after:content-[""]',
              'hover:after:w-[2px] hover:after:bg-foreground/50',
              resizing === c.key && 'after:w-[2px] after:bg-foreground/70',
            )}
          />
        </div>
      ))}
    </div>
  )
}

function Row({
  row,
  depth,
  canEdit,
  selected,
  people,
  linkOptions,
  onOpen,
  onSave,
  onSaveLinks,
  onFilterLink,
  expandable,
  expanded,
  onToggle,
  gridStyle,
  wrapNames,
}: {
  row: TaskRow
  depth: number
  canEdit: boolean
  selected: boolean
  people: TaskPerson[]
  linkOptions: TaskLinkOption[]
  onOpen: () => void
  onSave: SavePatch
  onSaveLinks: (kind: LinkKind, ids: string[]) => Promise<void>
  onFilterLink: (kind: LinkKind, id: string) => void
  expandable: boolean
  expanded: boolean
  onToggle: () => void
  gridStyle: CSSProperties
  wrapNames: boolean
}) {
  const ot = onTimeLabel(row.on_time)
  const links = {
    vehicle: linksOf(row, 'vehicle'),
    owner: linksOf(row, 'owner'),
    driver: linksOf(row, 'driver'),
  }
  return (
    <div
      className={cn(
        ROW_CLASS,
        'border-b border-border/60 px-3 text-sm hover:bg-muted/40',
        selected && 'bg-[hsl(var(--brand-soft))]/40',
        row.status === 'Cancelled' && 'opacity-60',
      )}
      style={gridStyle}
    >
      <Cell className="flex items-center gap-1">
        <span style={{ width: depth * 18 }} />
        {expandable ? (
          <button
            type="button"
            onClick={onToggle}
            className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={expanded ? 'Collapse subtasks' : 'Expand subtasks'}
          >
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        ) : depth > 0 ? (
          <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
        ) : (
          <span className="w-[18px] shrink-0" />
        )}
        {/* The reference, on the row at last.
            Until 2 September 2026 it appeared only in the detail panel header and
            the parent picker, so the number in a mention email ("task #27") could
            not be matched to anything on the board. Printed here, tabular and
            muted, so a person with the email open can find the card by eye. */}
        <span
          className="shrink-0 tabular-nums text-[11px] text-muted-foreground"
          title={`Task reference #${row.ref}`}
        >
          #{row.ref}
        </span>
        {/* The title truncates; the badges do not. They were inside the
            truncating element and the conflict warning on the longest title on
            the board, an unsubmitted insurance claim, was the first thing to
            disappear. A warning that hides on exactly the rows that need it is
            worse than no warning. */}
        <TitleCell
          value={row.title}
          save={onSave}
          disabled={!canEdit}
          wrap={wrapNames}
          className={cn(row.status === 'Done' && 'text-muted-foreground line-through')}
        />
        <button
          type="button"
          onClick={onOpen}
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Open this task"
          title="Open"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
        {row.subitem_count > 0 && (
          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
            {row.subitem_done_count}/{row.subitem_count}
          </span>
        )}
        {row.group_status_conflict && (
          <span
            className="inline-flex shrink-0 items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-200"
            title={`This card sits in the ${row.board_group} group but its status is ${row.status}. One of the two is wrong, and nothing has guessed which.`}
          >
            <TriangleAlert className="h-3 w-3" />
            <span className="hidden sm:inline">
              {row.board_group} vs {row.status}
            </span>
          </span>
        )}
        {/* Seventeen tasks came back out of Completed and Cancelled on 23 August
            when the group was made to follow the status, with no conversation
            attached to their return. This says which, rather than letting them
            melt into the Ongoing group. */}
        {row.restored_from_finished === true && (
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Icon only below sm. At 430px the columns are the same width
                  as on a desktop, so three words of badge left one letter of
                  title: "S…", "P…", "T…". Three of the six people who use this
                  board work from a phone. The words live in the tooltip. */}
              <span className="inline-flex shrink-0 items-center gap-1 rounded bg-fuchsia-100 px-1.5 py-0.5 text-[10px] font-medium text-fuchsia-900 dark:bg-fuchsia-950 dark:text-fuchsia-200">
                <RotateCcw className="h-3 w-3" />
                <span className="hidden sm:inline">back from the archive</span>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              This task was filed as finished and its status was not. It was moved back by{' '}
              {row.regrouped_by ?? 'somebody'}
              {row.regrouped_at ? ` on ${longDateTime(row.regrouped_at)}` : ''}. Nobody has said
              yet what still needs doing.
            </TooltipContent>
          </Tooltip>
        )}
        {/* Where this came from, and a way back to it. A content task that
            cannot be clicked through to the post is a task whose subject is
            somewhere else and the board will not say where. Arrives with
            db/migrations/0232. stopPropagation because the row itself opens the
            panel, and a click on the link must go to the link. */}
        {(() => {
          const o = originOf(row)
          if (!o) return null
          const cls =
            'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ' + o.tone
          return o.url ? (
            <a
              href={o.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className={cls + ' underline underline-offset-2'}
            >
              {o.label}
            </a>
          ) : (
            <span className={cls}>{o.label}</span>
          )
        })()}
        {row.source_doc_url && (
          <span className="shrink-0 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-800 dark:bg-violet-950 dark:text-violet-200">
            from meeting
          </span>
        )}
        {/* Why this row is in front of you when the Names me filter is on.
            An assignment and a mention are different things: one is yours to do,
            the other is yours to answer. Widening the board without saying which
            is which would leave the operator guessing. Strictly === true, because
            undefined means the handler did not report it and must not be drawn as
            a denial. */}
        {row.mentions_me === true && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex shrink-0 items-center gap-1 rounded bg-[hsl(var(--brand-soft))] px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--brand-soft-foreground))]">
                <AtSign className="h-3 w-3" />
                <span className="hidden sm:inline">names you</span>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Somebody named you in an update on this task. Open it to read the
              thread. This is not the same as being assigned to it.
            </TooltipContent>
          </Tooltip>
        )}
      </Cell>

      <Cell>
        <PeopleCell
          names={row.assignee_names}
          unlinked={row.assignee_unlinked}
          people={people}
          save={onSave}
          disabled={!canEdit}
          onClick={onOpen}
        />
      </Cell>
      <Cell>
        <StatusCell value={row.status} group={row.board_group} save={onSave} disabled={!canEdit} />
      </Cell>
      <Cell>
        <PriorityCell value={row.priority} save={onSave} disabled={!canEdit} />
      </Cell>
      <Cell>
        <AuthorityCell value={row.authority_level} save={onSave} disabled={!canEdit} />
      </Cell>
      <Cell>
        <LinkCell
          kinds={['vehicle', 'owner']}
          links={links}
          options={linkOptions}
          save={onSaveLinks}
          disabled={!canEdit}
          title="Vehicles and vehicle owners"
          onChipClick={onFilterLink}
        />
      </Cell>
      <Cell>
        <LinkCell
          kinds={['driver']}
          links={links}
          options={linkOptions}
          save={onSaveLinks}
          disabled={!canEdit}
          title="Drivers"
          onChipClick={onFilterLink}
        />
      </Cell>
      <Cell>
        <DateCell value={row.start_date} field="start_date" save={onSave} disabled={!canEdit} />
      </Cell>
      <Cell>
        <DateCell
          value={row.target_completion}
          field="target_completion"
          save={onSave}
          disabled={!canEdit}
          tone={row.is_overdue ? 'text-rose-600 dark:text-rose-400 font-semibold' : undefined}
        />
      </Cell>
      <Cell>
        {/* A finished task with no date is 25 rows on this board, every one of
            them a monday.com import whose export carried no completion date
            either. The date is not missing because somebody forgot, it does not
            exist, and a blank cell beside a Done pill says the opposite. Still
            editable: if anybody knows the real day, they can type it.
            Arrives with db/migrations/0232. */}
        <DateCell
          value={row.actual_completion}
          field="actual_completion"
          save={onSave}
          disabled={!canEdit}
          placeholder={row.completion_date_unknown === true ? 'not recorded' : undefined}
        />
      </Cell>
      <Cell>
        <div className={cn('truncate text-xs', ot.tone)} title={daysDeltaLabel(row.days_delta)}>
          {ot.text}
          {row.days_delta !== null && row.on_time !== 'Pending' && (
            <span className="ml-1 tabular-nums opacity-70">{daysDeltaShort(row.days_delta)}</span>
          )}
        </div>
      </Cell>
      {/* UNDEFINED IS NOT ZERO. The column arrives with db/migrations/0084, and
          until it does the count is undefined, which means "not known" and not
          "no files". A blank cell says nothing; a greyed paperclip would claim
          this task has been checked and has none. Same rule as the target date
          rendering as "no target" rather than as "on time". */}
      <Cell className="text-center">
        {row.attachment_count == null ? null : (
          <button
            type="button"
            onClick={onOpen}
            title={
              row.attachment_count === 0
                ? 'No files on this task'
                : `${row.attachment_count} file${row.attachment_count === 1 ? '' : 's'}`
            }
            className={cn(
              'inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-xs hover:bg-muted',
              row.attachment_count > 0 ? 'text-foreground' : 'text-muted-foreground/50',
            )}
          >
            <Paperclip className="h-3.5 w-3.5" />
            <span className="tabular-nums">{row.attachment_count || ''}</span>
          </button>
        )}
      </Cell>
      <Cell className="text-center">
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            'inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-xs hover:bg-muted',
            row.update_count > 0 ? 'text-foreground' : 'text-muted-foreground/50',
          )}
        >
          <MessageSquare className="h-3.5 w-3.5" />
          <span className="tabular-nums">{row.update_count || ''}</span>
        </button>
      </Cell>
      {/* Created and Updated, in this order because BOARD_COLUMNS ends with them.
          The header is built from that array and the row is written out by hand,
          so the two are kept in step only by both ending in the same place. If
          you insert a column in the middle of the array, insert its cell in the
          middle here too, or every column right of it renders under the wrong
          heading with nothing failing. */}
      <Cell>
        <StampCell value={row.created_at} />
      </Cell>
      <Cell>
        <StampCell value={row.updated_at} />
      </Cell>
    </div>
  )
}

export function TaskBoard({
  rows,
  canEdit,
  selectedId,
  people,
  linkOptions,
  onOpen,
  onSave,
  onSaveLinks,
  onFilterLink,
  onAddInGroup,
}: {
  rows: TaskRow[]
  canEdit: boolean
  selectedId: string | null
  people: TaskPerson[]
  linkOptions: TaskLinkOption[]
  onOpen: (id: string) => void
  onSave: (taskId: string, patch: Record<string, unknown>) => Promise<void>
  onSaveLinks: (taskId: string, kind: LinkKind, ids: string[]) => Promise<void>
  onFilterLink: (kind: LinkKind, id: string) => void
  onAddInGroup: (group: BoardGroup) => void
}) {
  // Completed and Cancelled start closed. They hold 228 of the 253 imported
  // rows, and opening the page onto four screens of finished work buries the
  // twenty five things that actually need doing.
  const [closed, setClosed] = useState<Record<string, boolean>>({
    Completed: true,
    Cancelled: true,
  })
  const [openSubs, setOpenSubs] = useState<Record<string, boolean>>({})

  // Read once, from this browser. See boardColumns.ts.
  const [widths, setWidths] = useState<ColumnWidths>(readColumnWidths)
  const [wrapNames, setWrapNames] = useState<boolean>(readWrapNames)
  const [resizing, setResizing] = useState<ColumnKey | null>(null)

  // One object, shared by the header and all 253 rows, so a drag re-renders with
  // one new style object rather than 254 of them.
  const gridStyle = useMemo<CSSProperties>(
    () => ({ gridTemplateColumns: columnTemplate(widths) }),
    [widths],
  )

  const startResize = (e: ReactPointerEvent, key: ColumnKey) => {
    // The handle sits inside the header cell. Without this, dragging it also
    // starts a text selection across the whole board.
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startWidth = widths[key]
    let latest = startWidth

    const move = (ev: PointerEvent) => {
      latest = clampWidth(key, startWidth + (ev.clientX - startX))
      setWidths((w) => ({ ...w, [key]: latest }))
    }
    const end = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', end)
      window.removeEventListener('pointercancel', end)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
      setResizing(null)
      // Stored once, when the drag finishes. Storing on every pointermove is
      // sixty writes a second of a value nothing reads until the next reload.
      writeColumnWidths({ ...widths, [key]: latest })
    }

    // Listeners on the window, not the handle: a fast drag leaves the two
    // pixel handle behind and the resize has to keep following the pointer.
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', end)
    window.addEventListener('pointercancel', end)
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
    setResizing(key)
  }

  const resetColumn = (key: ColumnKey) => {
    const next = { ...widths, [key]: defaultColumnWidths()[key] }
    setWidths(next)
    writeColumnWidths(next)
  }

  const resetAll = () => {
    clearColumnWidths()
    setWidths(defaultColumnWidths())
  }

  // Computed, set and stored in that order, NOT inside the state updater.
  // Storing from inside the updater put a side effect somewhere React is free to
  // call more than once, and it showed: the first toggle of the session turned
  // wrapping on on screen and wrote "off" to storage, so it came back off after
  // a reload. An updater has to be a pure function of the previous state.
  const toggleWrap = () => {
    const next = !wrapNames
    setWrapNames(next)
    writeWrapNames(next)
  }

  const groups = groupRows(rows)
  const subitemsOf = (id: string) => rows.filter((r) => r.parent_task_id === id)

  if (rows.length === 0) return null

  return (
    // The board is as wide as its columns say it is, and the container above it
    // scrolls. This used to be a flat min-w-[78rem], which is why widening a
    // column could not work: there was nowhere for the extra width to go.
    // px-3 on each row sits outside the columns, so it is added here.
    <div style={{ minWidth: boardWidth(widths) + 24 }}>
      <HeaderRow
        widths={widths}
        resizing={resizing}
        wrapNames={wrapNames}
        onResizeStart={startResize}
        onResetColumn={resetColumn}
        onResetAll={resetAll}
        onToggleWrap={toggleWrap}
      />
      {groups.map(({ group, rows: groupRowsList }) => {
        const isClosed = closed[group] === true
        const overdue = groupRowsList.filter((r) => r.is_overdue).length
        return (
          <div key={group}>
            <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-3 py-2">
              <button
                type="button"
                onClick={() => setClosed((c) => ({ ...c, [group]: !isClosed }))}
                className="flex items-center gap-2 text-sm font-semibold"
              >
                {isClosed ? (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
                <span className={cn('h-4 w-1 rounded-full', GROUP_BAR[group])} />
                {GROUP_LABEL[group]}
              </button>
              <span className="rounded bg-background px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
                {groupRowsList.length}
              </span>
              {/* Open was renamed to "Not started" and two groups are new. A
                  renamed heading with no explanation is how a team carries on
                  using the old meaning for a month. */}
              {GROUP_NOTE[group] && (
                <span className="hidden text-xs text-muted-foreground sm:inline">
                  {GROUP_NOTE[group]}
                </span>
              )}
              {overdue > 0 && (
                <span className="rounded bg-rose-100 px-1.5 py-0.5 text-xs font-medium tabular-nums text-rose-800 dark:bg-rose-950 dark:text-rose-200">
                  {overdue} overdue
                </span>
              )}
              {canEdit && (
                <button
                  type="button"
                  onClick={() => onAddInGroup(group)}
                  className="ml-auto inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add task
                </button>
              )}
            </div>

            {!isClosed &&
              groupRowsList.map((row) => {
                const subs = subitemsOf(row.id)
                const expanded = openSubs[row.id] === true
                return (
                  <div key={row.id}>
                    <Row
                      row={row}
                      depth={0}
                      canEdit={canEdit}
                      selected={selectedId === row.id}
                      people={people}
                      linkOptions={linkOptions}
                      onOpen={() => onOpen(row.id)}
                      onSave={(patch) => onSave(row.id, patch)}
                      onSaveLinks={(kind, ids) => onSaveLinks(row.id, kind, ids)}
                      onFilterLink={onFilterLink}
                      expandable={subs.length > 0}
                      expanded={expanded}
                      onToggle={() => setOpenSubs((s) => ({ ...s, [row.id]: !expanded }))}
                      gridStyle={gridStyle}
                      wrapNames={wrapNames}
                    />
                    {expanded &&
                      subs.map((sub) => (
                        <Row
                          key={sub.id}
                          row={sub}
                          depth={1}
                          canEdit={canEdit}
                          selected={selectedId === sub.id}
                          people={people}
                          linkOptions={linkOptions}
                          onOpen={() => onOpen(sub.id)}
                          onSave={(patch) => onSave(sub.id, patch)}
                          onSaveLinks={(kind, ids) => onSaveLinks(sub.id, kind, ids)}
                          onFilterLink={onFilterLink}
                          expandable={false}
                          expanded={false}
                          onToggle={() => {}}
                          gridStyle={gridStyle}
                          wrapNames={wrapNames}
                        />
                      ))}
                  </div>
                )
              })}
          </div>
        )
      })}
    </div>
  )
}
